
import React, { useState, useEffect, useRef } from 'react';
import { extractJobAdvert, generateQuestions } from '@shared/services/gemini';
import { NHSGrade, InterviewType, Journey, KnowledgeDoc } from '@shared/types';
import { speakText } from '@shared/utils/voice';
import { SPECIALTIES, GRADES, INTERVIEW_TYPES } from '../constants';
import { extractTextFromPdf } from '@shared/utils/pdf';

interface Props {
  onSubmit: (j: Journey) => void;
  onCancel: () => void;
  knowledgeDocs: KnowledgeDoc[];
  userCredits: number;
  journeys: Journey[];
}

const LOADING_MESSAGES = [
  "Extracting 'Interview DNA' from Job Advertisement...",
  "Analyzing Professional Portfolio & Evidence Grounding...",
  "Mapping Competencies to NHS Grade Standards...",
  "Generating Stations Set 1: Portfolio & CAMP focus...",
  "Synthesizing Stations Set 2: Clinical Scenarios & SPIES...",
  "Constructing Stations Set 3: Governance & CARE focus...",
  "Drafting Stations Set 4: Ethics & Law dilemmas...",
  "Applying 'Hurdle Logic' to Follow-up Threads...",
  "Calibrating Marking Rubrics to Professional Standards...",
  "Finalizing Custom Curriculum & Model Answers..."
];

const JourneyForm: React.FC<Props> = ({ onSubmit, onCancel, knowledgeDocs, userCredits, journeys }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Determine an initial count that the user can actually afford
  const initialCount = userCredits >= 20 ? 20 : 10;

  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent);
  const [formData, setFormData] = useState({
    grade: NHSGrade.CT,
    specialty: 'Acute Internal Medicine',
    type: InterviewType.Mixed,
    advertText: '',
    profile: '',
    difficulty: 'Intermediate' as const,
    questionCount: initialCount,
    isRealInterview: false,
    voiceName: isIOS ? 'David' : 'Google UK English Female',
    voiceRate: isIOS ? 1.2 : 1.1,
    voicePitch: 1.05
  });
  const [additionalDocs, setAdditionalDocs] = useState<KnowledgeDoc[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const additionalInputRef = useRef<HTMLInputElement>(null);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);

  const cost = formData.questionCount;
  const hasEnoughCredits = userCredits >= cost;
  const totalCharCount = [
    formData.advertText,
    formData.profile,
    ...additionalDocs.map(d => d.content || '')
  ].join('').length;
  const maxChars = 50000;
  const isOverCharLimit = totalCharCount > maxChars;

  useEffect(() => {
    let interval: any;
    if (loading) {
      interval = setInterval(() => {
        setLoadingMsgIdx((prev) => (prev + 1) % LOADING_MESSAGES.length);
      }, 2500);
    } else {
      setLoadingMsgIdx(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const enGbVoices = (voices || []).filter(v => v.lang === 'en-GB' || v.lang.includes('en-GB'));
      const iOSVoices = isIOS ? enGbVoices.filter(v => v.name.toLowerCase().includes('david')) : enGbVoices;
      setAvailableVoices(iOSVoices);
      if (isIOS && formData.voiceName !== 'David') {
        setFormData(prev => ({ ...prev, voiceName: 'David', voiceRate: 1.2 }));
      }
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [formData.voiceName]);

  const handleAdditionalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are supported.');
      return;
    }

    setUploadingDoc(true);
    setUploadStatus('Extracting content...');
    try {
      const extractedText = await extractTextFromPdf(file);
      const newDoc: KnowledgeDoc = {
        id: Math.random().toString(36).substr(2, 9),
        userId: 'temp',
        title: file.name,
        content: extractedText,
        tags: ['Session Upload'],
        isSystem: false,
        mimeType: file.type
      };
      setAdditionalDocs(prev => [...prev, newDoc]);
      setUploadStatus('');
    } catch (err: any) {
      console.error(err);
      alert(`Error processing PDF: ${err.message}`);
    } finally {
      setUploadingDoc(false);
      setUploadStatus('');
      if (additionalInputRef.current) additionalInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    if (!hasEnoughCredits) {
      setError(`Yetersiz kredi! Bu işlem için ${cost} kredi gerekiyor. Sizin krediniz: ${userCredits}.`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const requirements = await extractJobAdvert(formData.advertText);

      const recentJourneys = journeys
        .filter(j => !j.isPublic)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 2);
      const avoidQuestions = recentJourneys
        .flatMap(j => j.questions?.map(q => q.text) || [])
        .filter(Boolean);

      const tempDocs: KnowledgeDoc[] = [
        ...knowledgeDocs,
        ...additionalDocs,
        { id: 'temp-profile', userId: 'temp', title: 'Session Profile', content: formData.profile, tags: [], isSystem: false }
      ];

      const questions = await generateQuestions(
        formData.grade, 
        formData.specialty, 
        formData.type, 
        requirements,
        tempDocs,
        formData.questionCount,
        avoidQuestions
      );

      if (!questions || questions.length === 0) {
        throw new Error("AI failed to generate question set.");
      }

      const journey: Journey = {
        id: Math.random().toString(36).substr(2, 9),
        userId: '',
        title: formData.type === InterviewType.Mixed
          ? `${formData.specialty} Mock (${formData.questionCount} Stations)`
          : `${formData.specialty} ${formData.type} Focus`,
        grade: formData.grade,
        specialty: formData.specialty,
        interviewType: formData.type,
        isRealInterview: formData.isRealInterview,
        voiceSettings: formData.isRealInterview ? {
          voiceName: formData.voiceName || undefined,
          rate: formData.voiceRate,
          pitch: formData.voicePitch
        } : undefined,
        additionalDocs: additionalDocs.length ? additionalDocs : undefined,
        difficulty: formData.difficulty as any,
        requirements,
        questions,
        candidateProfile: formData.profile,
        createdAt: new Date().toISOString(),
        isPublic: false
      };

      onSubmit(journey);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "An unexpected error occurred with the AI Engine.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/95 backdrop-blur-xl z-[100] flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-500">
          <div className="relative mb-12">
            <div className="w-24 h-24 border-4 border-slate-100 rounded-full"></div>
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-blue-600 font-black text-xl">M</span>
            </div>
          </div>
          
          <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">
            Generating custom curriculum for {formData.questionCount} stations...
          </h2>
          
          <div className="min-h-[60px] flex flex-col items-center">
            <p className="text-blue-600 font-black text-xs uppercase tracking-[0.2em] mb-3 animate-pulse">
              AI Panel Analysis In Progress
            </p>
            <p className="text-slate-500 max-w-md text-xl font-bold leading-relaxed italic animate-in fade-in slide-in-from-bottom-2 duration-500" key={loadingMsgIdx}>
              "{LOADING_MESSAGES[loadingMsgIdx]}"
            </p>
          </div>
        </div>
      )}

      <div className="mb-10 flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-slate-900 mb-2">Configure Practice Track</h2>
          <p className="text-slate-500 font-medium">Bespoke curriculum mapping for {formData.specialty}.</p>
        </div>
        <div className={`bg-white border-2 px-6 py-3 rounded-3xl text-right transition-colors ${hasEnoughCredits ? 'border-slate-100' : 'border-red-100 bg-red-50'}`}>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Maliyet / Bakiyeniz</p>
          <p className={`text-xl font-black ${hasEnoughCredits ? 'text-blue-600' : 'text-red-500'}`}>
            {cost} / {userCredits} Kredi
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-12">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-sm transition-colors ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>1</div>
        <div className="flex-grow h-0.5 bg-slate-100"></div>
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-sm transition-colors ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>2</div>
      </div>

      {step === 1 ? (
        <div className="space-y-10 animate-in fade-in slide-in-from-right-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Target Grade</label>
              <select 
                value={formData.grade}
                onChange={e => setFormData({...formData, grade: e.target.value as NHSGrade})}
                className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold shadow-inner transition-all"
              >
                {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Specialty / Field</label>
              <select 
                value={formData.specialty}
                onChange={e => setFormData({...formData, specialty: e.target.value})}
                className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold shadow-inner transition-all"
              >
                {SPECIALTIES.sort().map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
               <label className="block text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Practice Mode</label>
               <select 
                value={formData.type}
                onChange={e => setFormData({...formData, type: e.target.value as InterviewType})}
                className="w-full p-5 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold shadow-inner transition-all"
               >
                 <option value={InterviewType.Mixed}>Full Mock Simulation (Balanced Ratio)</option>
                 {INTERVIEW_TYPES.filter(t => t !== InterviewType.Mixed).map(t => (
                    <option key={t} value={t}>{t} Specialization</option>
                 ))}
               </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">Station Count (Cost: 1 Credit/Station)</label>
              <div className="grid grid-cols-2 gap-3">
                {[10, 20].map(count => {
                  const isAffordable = userCredits >= count;
                  return (
                    <button
                      key={count}
                      disabled={!isAffordable}
                      onClick={() => setFormData({...formData, questionCount: count})}
                      className={`py-4 rounded-2xl font-black text-sm transition-all border-2 
                        ${formData.questionCount === count 
                          ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-100' 
                          : isAffordable 
                            ? 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-200' 
                            : 'bg-slate-100 border-slate-100 text-slate-200 cursor-not-allowed opacity-50'
                        }`}
                      title={isAffordable ? `${count} Stations` : `Needs ${count} credits (You have ${userCredits})`}
                    >
                      {count}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-8 border-t border-slate-50">
            <button onClick={onCancel} className="text-slate-400 font-bold hover:text-slate-600 transition-colors uppercase tracking-widest text-[10px]">Cancel</button>
            <button 
              onClick={() => setStep(2)} 
              className="bg-slate-900 text-white px-10 py-5 rounded-2xl font-black shadow-2xl shadow-slate-200 hover:bg-blue-600 transition-all flex items-center gap-3 group active:scale-95"
            >
              Next Step: Personalize Context
              <svg className="group-hover:translate-x-1 transition-transform" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">1. Job Advert / Specification</label>
                <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded font-bold uppercase">Required</span>
              </div>
              <textarea 
                rows={12}
                placeholder="Paste Job Description..."
                value={formData.advertText}
                onChange={e => setFormData({...formData, advertText: e.target.value})}
                className="w-full p-6 bg-slate-50 border-2 border-slate-50 rounded-[2.5rem] focus:border-blue-500 focus:bg-white outline-none font-medium text-slate-700 text-sm leading-relaxed shadow-inner transition-all"
              ></textarea>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">2. Your Profile / CV Highlights</label>
                <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-bold uppercase">Bespoke Logic</span>
              </div>
              <textarea 
                rows={12}
                placeholder="Paste CV or personal statement..."
                value={formData.profile}
                onChange={e => setFormData({...formData, profile: e.target.value})}
                className="w-full p-6 bg-slate-50 border-2 border-slate-50 rounded-[2.5rem] focus:border-emerald-500 focus:bg-white outline-none font-medium text-slate-700 text-sm leading-relaxed shadow-inner transition-all"
              ></textarea>
            </div>
          </div>

          {!hasEnoughCredits && (
            <div className="bg-amber-50 border-2 border-amber-100 p-6 rounded-[2rem] flex items-center justify-between animate-in slide-in-from-top-2">
              <div className="flex items-center gap-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <div>
                  <p className="text-amber-900 text-sm font-bold">Insufficient Balance</p>
                  <p className="text-amber-700 text-xs">This track requires {cost} credits. You have {userCredits}.</p>
                </div>
              </div>
              <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Add credits in header</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-2 border-red-100 p-6 rounded-[2rem] flex items-center gap-4 animate-in slide-in-from-top-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p className="text-red-900 text-sm font-bold leading-relaxed">{error}</p>
            </div>
          )}

          {isOverCharLimit && (
            <div className="bg-red-50 border-2 border-red-100 p-6 rounded-[2rem] flex items-center gap-4 animate-in slide-in-from-top-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p className="text-red-900 text-sm font-bold leading-relaxed">Total character limit exceeded ({totalCharCount.toLocaleString()} / {maxChars.toLocaleString()}). Please shorten Job Advert, CV, or Additional Documents.</p>
            </div>
          )}

          <div className="bg-white border-2 border-slate-100 p-6 rounded-[2rem] space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">3. Additional Documents (Optional)</label>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded font-bold uppercase">PDF</span>
            </div>
            <div className="flex items-center gap-4">
              <input type="file" ref={additionalInputRef} onChange={handleAdditionalUpload} className="hidden" accept="application/pdf" />
              <button
                type="button"
                onClick={() => additionalInputRef.current?.click()}
                disabled={uploadingDoc}
                className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-blue-600 disabled:opacity-60"
              >
                {uploadingDoc ? (uploadStatus || 'Processing...') : 'Upload PDF'}
              </button>
              <p className="text-xs text-slate-500 font-medium">Used only for this interview.</p>
            </div>
            {additionalDocs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {additionalDocs.map(doc => (
                  <span key={doc.id} className="text-[10px] font-black bg-slate-50 text-slate-500 px-3 py-1 rounded-full border border-slate-100 uppercase tracking-tighter flex items-center gap-2">
                    {doc.title}
                    <button
                      type="button"
                      onClick={() => setAdditionalDocs(prev => prev.filter(d => d.id !== doc.id))}
                      className="text-slate-400 hover:text-red-500"
                      title="Remove"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className={`text-[10px] font-bold uppercase tracking-widest ${isOverCharLimit ? 'text-red-600' : 'text-slate-400'}`}>
              Total characters: {totalCharCount.toLocaleString()} / {maxChars.toLocaleString()}
            </p>
          </div>

          <div className="bg-slate-50 border-2 border-slate-100 p-6 rounded-[2rem] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Real Interview</p>
              <p className="text-sm font-bold text-slate-700">Voice-only interview (questions hidden).</p>
            </div>
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRealInterview}
                onChange={e => setFormData({ ...formData, isRealInterview: e.target.checked })}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="text-xs font-black uppercase tracking-widest text-slate-500">Enable</span>
            </label>
          </div>

          {formData.isRealInterview && (
            <div className="bg-white border-2 border-slate-100 p-6 rounded-[2rem] space-y-4">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Voice Settings</p>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 tracking-widest">Voice</label>
                  <select
                    value={formData.voiceName}
                    onChange={e => setFormData({ ...formData, voiceName: e.target.value })}
                    className="w-full p-4 bg-slate-50 border-2 border-slate-50 rounded-2xl focus:border-blue-500 focus:bg-white outline-none font-bold shadow-inner transition-all"
                  >
                    <option value="">Auto (best available)</option>
                    {availableVoices.map(v => (
                      <option key={`${v.name}-${v.lang}`} value={v.name}>
                        {v.name} ({v.lang})
                      </option>
                    ))}
                  </select>
                  {availableVoices.length === 0 && (
                    <p className="text-[10px] text-slate-400 mt-2">Voices are loading. If empty, try Edge/Chrome.</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => speakText('This is a voice preview for your real interview.', {
                    voiceName: formData.voiceName || undefined,
                    rate: formData.voiceRate,
                    pitch: formData.voicePitch
                  })}
                  className="self-start md:self-end bg-blue-600 text-white px-4 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                  Test Voice
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 tracking-widest">Rate</label>
                  <input
                    type="range"
                    min="0.8"
                    max="1.4"
                    step="0.05"
                    value={formData.voiceRate}
                    onChange={e => setFormData({ ...formData, voiceRate: Number(e.target.value) })}
                    className="w-full accent-blue-600"
                  />
                  <p className="text-xs font-bold text-slate-500 mt-1">{formData.voiceRate.toFixed(2)}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 tracking-widest">Pitch</label>
                  <input
                    type="range"
                    min="0.8"
                    max="1.2"
                    step="0.05"
                    value={formData.voicePitch}
                    onChange={e => setFormData({ ...formData, voicePitch: Number(e.target.value) })}
                    className="w-full accent-blue-600"
                  />
                  <p className="text-xs font-bold text-slate-500 mt-1">{formData.voicePitch.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-8 border-t border-slate-50">
            <button onClick={() => setStep(1)} className="text-slate-400 font-bold hover:text-slate-600 transition-colors uppercase tracking-widest text-[10px]">Back</button>
            <button 
              onClick={handleCreate} 
              disabled={loading || !formData.advertText || !hasEnoughCredits || isOverCharLimit}
              className="bg-blue-600 text-white px-12 py-5 rounded-[2rem] font-black shadow-2xl shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center gap-3 active:scale-95"
            >
              Generate {formData.questionCount} Custom Stations
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyForm;
